import React, { useState } from 'react';
import { Lightbulb, Loader2, CheckCircle2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { submitFeatureRequest } from '../../lib/featureRequestApi';
import { toast } from 'sonner';

export default function FeatureRequestButton() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (!message.trim()) {
      toast.error('Please describe your idea first.');
      return;
    }
    setLoading(true);
    try {
      await submitFeatureRequest(message.trim(), email.trim());
      setSubmitted(true);
      setMessage('');
      setEmail('');
    } catch (e) {
      toast.error(e.message || 'Could not submit your idea. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (next) => {
    setOpen(next);
    if (!next) setSubmitted(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center justify-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-800 transition-colors min-h-[40px] min-w-[40px] sm:min-h-0 sm:min-w-0 px-3 py-1.5 rounded-full hover:bg-neutral-100"
        data-testid="feature-request-button"
      >
        <Lightbulb className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Ideas</span>
      </button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent data-testid="feature-request-modal">
          {submitted ? (
            <div className="py-4 text-center space-y-2" data-testid="feature-request-success">
              <CheckCircle2 className="w-8 h-8 text-green-600 mx-auto" />
              <p className="text-sm font-medium text-neutral-800">Thanks for the idea!</p>
              <p className="text-xs text-neutral-500">We read every suggestion.</p>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Suggest a feature</DialogTitle>
                <DialogDescription>
                  Got an idea to make Waxify better? Tell us about it.
                </DialogDescription>
              </DialogHeader>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="I'd love it if Waxify could..."
                rows={4}
                maxLength={2000}
                data-testid="feature-request-message-input"
              />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email (optional, in case we follow up)"
                className="w-full h-10 px-3 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400"
                data-testid="feature-request-email-input"
              />
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                className="flex items-center justify-center gap-2 w-full h-11 rounded-lg bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white text-sm font-semibold transition-colors"
                data-testid="feature-request-submit-button"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {loading ? 'Submitting…' : 'Submit idea'}
              </button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
